/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { IconTile } from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import VideoIcon from '@atlaskit/icon/core/video';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type ExternalChapter, type SearchResultVideoPartial } from '@atlassian/search-client';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../../../../../controllers/store/search';

import { messages } from './messages';
import { convertVideoDuration, createChapterUrl, isLoomUrlExpired } from './utils';

interface LoomSearchResultProps {
	partial: SearchResultVideoPartial;
	/** The position in the results list shown to the user. */
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
}

const thumbnailContainerStyles = xcss({
	position: 'relative',
	gridArea: 'body',
});

const thumbnailImageStyles = css({
	borderRadius: token('radius.small'),
	maxHeight: '6.5rem',
	width: 'auto',
});

const bodyAreaStyles = xcss({
	display: 'flex',
	columnGap: 'space.150',
});

const VideoThumbnail = ({ thumbnailUrl, url }: { thumbnailUrl?: string; url: string }) => {
	const { formatMessage } = useIntl();
	const [isErrored, setIsErrored] = useState(false);

	return !thumbnailUrl || isLoomUrlExpired(thumbnailUrl) || isErrored ? null : (
		<Inline testId="thumbnail-container" xcss={thumbnailContainerStyles}>
			<Anchor href={url} rel={'noreferrer'} target={'_blank'}>
				<img
					css={thumbnailImageStyles}
					src={thumbnailUrl}
					alt={formatMessage(messages.videoThumbnailAltText)}
					onError={() => setIsErrored(true)}
				/>
			</Anchor>
		</Inline>
	);
};

const footerContainer = xcss({
	paddingTop: 'space.150',
	position: 'relative',
});

const VideoChapters = ({ chapters, url }: { chapters?: ExternalChapter[]; url: string }) => {
	if (!chapters || chapters.length === 0) {
		return null;
	}

	return (
		<Box xcss={footerContainer}>
			<DropdownMenu chapters={chapters} url={url}></DropdownMenu>
		</Box>
	);
};

const DropdownMenu = ({ chapters, url }: { chapters: ExternalChapter[]; url: string }) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const dropdownMessage = formatMessage(messages.dropdownMessage, {
		chapterCount: chapters.length,
	});

	return (
		<Box>
			<Button
				iconAfter={(iconProps) =>
					isExpanded ? (
						<ChevronDownIcon {...iconProps} size="small" />
					) : (
						<ChevronRightIcon {...iconProps} size="small" />
					)
				}
				onClick={() => setIsExpanded(!isExpanded)}
				isSelected={isExpanded}
			>
				{dropdownMessage}
			</Button>
			{isExpanded && <DropdownItems chapters={chapters} url={url} />}
		</Box>
	);
};

const chapterLinkStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',

	':hover': {
		color: 'color.link',
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
});

const dropdownItemStyles = xcss({
	display: 'flex',
	columnGap: 'space.025',
	paddingBlock: 'space.050',
	marginTop: 'space.100',
});

const seeAllStyles = xcss({
	textDecoration: 'none',
	marginTop: 'space.150',
	font: token('font.body.small'),
});

const DropdownItems = ({ chapters, url }: { chapters: ExternalChapter[]; url: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{chapters.map(({ title, startTimeInSeconds }) => {
				return (
					<Box xcss={dropdownItemStyles}>
						<IconTile
							size="16"
							icon={VideoIcon}
							label={formatMessage(messages.videoIconLabel)}
							appearance="blue"
						/>
						<Inline space={'space.050'}>
							<Text size="small" weight="medium">
								<Anchor
									xcss={chapterLinkStyles}
									href={createChapterUrl(url, startTimeInSeconds)}
									rel={'noreferrer'}
									target="_blank"
								>
									{convertVideoDuration(startTimeInSeconds)}
								</Anchor>
							</Text>
							<Text size="small">{title}</Text>
						</Inline>
					</Box>
				);
			})}
			{chapters.length > 10 && (
				<Box xcss={seeAllStyles}>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Anchor href={url} target="_blank" xcss={seeAllStyles}>
						See all
					</Anchor>
				</Box>
			)}
		</Box>
	);
};

export const VideoSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
}: LoomSearchResultProps) => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();
	const appMode = useApplicationMode();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { lastModifiedDate, url, permissionLevel, entity } = partial;
	const { durationInSeconds, chapters, createdBy, thumbnailUrl } = entity || {};
	const creatorName = createdBy?.user?.name || createdBy?.thirdPartyUser?.name || null;

	let { title, excerpt } = partial;

	const videoDuration = convertVideoDuration(durationInSeconds, { formatMessage, readable: true });

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const body = (
		<Box testId="thumbnail-container" xcss={bodyAreaStyles}>
			<VideoThumbnail thumbnailUrl={thumbnailUrl} url={url} />
			{thumbnailUrl && !isLoomUrlExpired(thumbnailUrl) ? (
				<Box>{processExcerpt(excerpt, true)} </Box>
			) : (
				<DescriptionBody description={processExcerpt(excerpt, true)} />
			)}
		</Box>
	);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const headerMetadataParts = [
		formatMessage(messages.videoLabel),
		['creator-name', creatorName],
		['last-modified', lastModifiedComponent],
		['video-duration', videoDuration],
		['padlock', padlockIcon],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={body}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			footer={<VideoChapters chapters={chapters} url={url} />}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
		/>
	);
};
